import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const RemoveFromParentForm = ({
  open = true,
  onClose,
  onRemove,
  entity,
  entityLabel = "User",
  parentLabel = "Item",
  assignments = [],
  parentKey = "id",
  parentMap = {},
  displayField = "username",
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const handleRemove = async () => {
    if (!selectedId) return;

    try {
      setLoading(true);
      await onRemove(selectedId, entity);
    } finally {
      setLoading(false);
    }
  };

  const parentList =
    assignments
      ?.filter((a) => a.active).map((a) => {
        const id = a[parentKey];
        const name = parentMap?.[id];
        return name ? { id, name } : null;
      })
      .filter(Boolean) || [];

  useEffect(() => {
    if (parentList.length === 1) {
      setSelectedId(parentList[0].id);
    }
  }, [parentList]);

  const displayName = entity?.[displayField] || entity?.name;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {" "}
      <DialogTitle>
        {" "}
        <SoftTypography variant="h6" fontWeight="bold" color="error">
          Remove {entityLabel} from {parentLabel}{" "}
        </SoftTypography>{" "}
      </DialogTitle>
      <DialogContent dividers>
        <SoftTypography variant="body2" mb={2}>
          {parentList.length > 1
            ? `Select ${parentLabel} to remove ${displayName} from:`
            : `Are you sure you want to remove ${displayName} from:`}
        </SoftTypography>

        {parentList.length > 1 && (
          <RadioGroup value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
            {parentList.map((parent) => (
              <FormControlLabel
                key={parent.id}
                value={parent.id}
                control={<Radio />}
                label={parent.name}
              />
            ))}
          </RadioGroup>
        )}

        {parentList.length === 1 && (
          <SoftTypography fontWeight="bold">{parentList[0].name}</SoftTypography>
        )}

        {parentList.length === 0 && (
          <SoftTypography variant="caption">No {parentLabel.toLowerCase()} assigned</SoftTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </SoftButton>

        <SoftButton
          variant="gradient"
          color="error"
          onClick={handleRemove}
          disabled={loading || !selectedId}
        >
          {loading ? "Removing..." : "Remove"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

RemoveFromParentForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  entity: PropTypes.object,
  entityLabel: PropTypes.string,
  parentLabel: PropTypes.string,
  assignments: PropTypes.array,
  parentKey: PropTypes.integer,
  parentMap: PropTypes.object,
  displayField: PropTypes.string,
};

export default RemoveFromParentForm;
